/**
 * Pure helpers for the bulk-patch org picker.
 *
 * Extracted from UiDrivenBulkPatchFlow so the "active org" + "parent
 * label" rules can be unit-tested without spinning up the whole app.
 *
 * Rules (see the bulk-patch revision spec):
 *   - A "Parent" is labeled strictly on the four parent-client types.
 *   - The picker lists only orgs that have content to edit — i.e. orgs
 *     that are "active" themselves, or that have at least one active
 *     child (an org whose contractedBy references this org's org_uuid).
 *   - A product is active when today falls in its start/end range, its
 *     own status isn't terminal, AND its deal isn't terminal.
 */

import { isActiveThisMonth } from 'utils/formatDayjsDateTime';

// Terminal sets mirror the bulk-patch candidates service so the picker's
// notion of "active" matches what the candidates endpoint will surface.
export const TERMINAL_DEAL_STATUSES = new Set([
  'Ended',
  'Cancelled',
  'Canceled',
  'Replaced',
]);
export const TERMINAL_PRODUCT_STATUSES = new Set([
  'Ended',
  'Cancelled',
  'Expired',
]);

// A "Parent" is labeled strictly on these org types. Mirrors the backend
// parentClients cache (parentClients.ts) — nothing else earns the chip.
export const PARENT_TYPES = new Set([
  'Property Management Company',
  'Ownership Group',
  'Agency Whitelabel',
  'Parent Client',
]);

export interface OrgOption {
  pk: string;
  name: string;
  type?: string;
  org_uuid: string;
  isParent: boolean;
}

type Row = Record<string, unknown>;

/**
 * Build the active-org option list for the picker.
 *
 * @param organizations org rows (need PK, org_uuid, name, type, contractedBy)
 * @param deals         deal rows (need deal_uuid, dealStatus)
 * @param products      product rows (need PK, deal_uuid, status, startDate, endDate)
 */
export function computeActiveOrgOptions(
  organizations: Row[] | undefined | null,
  deals: Row[] | undefined | null,
  products: Row[] | undefined | null
): OrgOption[] {
  if (!organizations) return [];

  // deal_uuid → dealStatus, so a product on a terminal deal can be rejected
  // even if the product's own status/dates still look active.
  const dealStatusByUuid = new Map<string, string>();
  for (const d of deals ?? []) {
    const uuid = String(d.deal_uuid ?? '');
    if (uuid) dealStatusByUuid.set(uuid, String(d.dealStatus ?? ''));
  }

  // Org PKs that directly contain at least one active product: today falls
  // in the product's date range, the product isn't terminal, and its deal
  // isn't terminal.
  const activeOrgPks = new Set<string>();
  for (const p of products ?? []) {
    const orgPk = String(p.PK ?? '');
    if (!orgPk || activeOrgPks.has(orgPk)) continue;
    if (TERMINAL_PRODUCT_STATUSES.has(String(p.status ?? ''))) continue;
    const dealStatus = dealStatusByUuid.get(String(p.deal_uuid ?? ''));
    if (dealStatus && TERMINAL_DEAL_STATUSES.has(dealStatus)) continue;
    if (!isActiveThisMonth(p.startDate as string, p.endDate as string)) continue;
    activeOrgPks.add(orgPk);
  }

  // Parents often carry no products of their own — the business sits on
  // child orgs (those whose contractedBy references the parent's org_uuid).
  // Roll a child's active-ness up so a parent with any active child stays
  // in the picker.
  const parentsWithActiveChild = new Set<string>();
  for (const o of organizations) {
    const pk = String(o.PK ?? '');
    if (!pk || !activeOrgPks.has(pk)) continue;
    const contractedBy = o.contractedBy;
    const parentIds = Array.isArray(contractedBy)
      ? contractedBy.map(String)
      : contractedBy
        ? [String(contractedBy)]
        : [];
    for (const pid of parentIds) if (pid) parentsWithActiveChild.add(pid);
  }

  const options: OrgOption[] = [];
  for (const o of organizations) {
    const pk = String(o.PK ?? '');
    if (!pk) continue;
    const orgUuid = String(o.org_uuid ?? '');
    const isActive =
      activeOrgPks.has(pk) || parentsWithActiveChild.has(orgUuid);
    if (!isActive) continue;
    options.push({
      pk,
      name: String(o.name ?? '(unnamed)'),
      type: o.type ? String(o.type) : undefined,
      org_uuid: orgUuid,
      isParent: PARENT_TYPES.has(String(o.type ?? '')),
    });
  }
  return options.sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  );
}
